import type { InternalRefetchQueriesInclude } from "@apollo/client";
import Avatar from "components/core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider, MenuTitle } from "components/core/menu";
import ActionsDropdown from "components/discussions/ActionsDropdown";
import Editor from "components/editor/Editor";
import ForumEditPost from "components/forum/FormEditPost";
import usePostActions from "components/forum/hooks/usePostActions";
import PostReactions from "components/reactions/PostReactions";
import classNames from "components/ui/classNames";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "components/ui/Dialog";
import RelativeTime from "components/ui/RelativeTime";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import parseISO from "date-fns/parseISO";
import type { IPostReplyFieldsFragment } from "lib/graphql/fragments/engagement/__generated__/PostReplyFields.generated";
import type { IPostRepliesQuery } from "lib/graphql/queries/engagement/__generated__/PostRepliesQuery.generated";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import PostsQuery from "lib/graphql/queries/engagement/PostsQuery";
import useSelfie from "lib/hooks/useSelfie";
import { CheckCircleIcon, CheckIcon, ClipboardIcon, PinIcon, ReplyIcon } from "lucide-react";
import { useRouter } from "next/router";
import React, { useState } from "react";
import TextareaAutosize from "react-autosize-textarea/lib";
import { FormattedMessage } from "react-intl";
import type { IPostContextType } from "types/graphqlTypes";
import { IReactionSubject } from "types/graphqlTypes";

interface ForumTopicCardProps {
  topic: NonNullable<IPostRepliesQuery["postByShortId"]> | IPostReplyFieldsFragment;
  postShortId: string;
  url: string;
  contextKey?: string | null;
  contextType: IPostContextType;
  className?: string;
  children?: React.ReactNode;
  onReply?: () => void;
}

export default function ForumTopicCard({
  topic,
  children,
  className,
  postShortId,
  url,
  contextKey = null,
  contextType,
  onReply,
}: ForumTopicCardProps) {
  const viewer = useSelfie();
  const router = useRouter();

  const {
    isDestroyingPost,
    isUnpinningPost,
    isPinningPost,
    isUnlockingPost,
    isLockingPost,
    isDestroyingPostReply,
    isPinningPostReply,
    isUnpinningPostReply,
    isSettingAnswerPostReply,
    isUnsettingAnswerPostReply,

    destroyPostReply,
    destroyPost,
    pinPost,
    unpinPost,
    unlockPost,
    lockPost,
    pinPostReply,
    unpinPostReply,
    setPostReplyAnswer,
    unsetPostReplyAnswer,
  } = usePostActions();
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isOpenShare, setIsOpenShare] = useState<boolean>(false);

  const viewerCanTakeActions = topic.viewerCanEdit || topic.viewerCanDestroy || (viewer && viewer.isSiteAdmin);
  const viewerCanTakeAnswerActions = topic.viewerDidAuthor || (viewer && viewer.isSiteAdmin);

  const loadingAnswer = isSettingAnswerPostReply || isUnsettingAnswerPostReply;

  const refetchQueries: InternalRefetchQueriesInclude = [
    {
      query: PostRepliesQuery,
      variables: {
        postShortId,
        cursor: null,
      },
    },
    {
      query: PostsQuery,
      variables: {
        contextKey,
        contextType,
        limit: 30,
        cursor: null,
      },
    },
  ];

  if (topic.__typename !== "ForumPost" && topic.__typename !== "PostReply") {
    return null;
  }

  return (
    <div
      id={`reply-${topic._id}-${parseISO(topic.createdAt).getTime()}`}
      className={classNames(
        "group relative col-span-4 rounded-2xl md:col-span-9",
        "border border-neutral-50 p-4 lg:p-8",
        "border-slate-300 bg-white",
        "dark:border-slate-900 dark:bg-slate-800",
        "overflow-hidden",
        className,
      )}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-5 gap-y-4">
        <Avatar
          key={topic.author._id}
          src={topic.author.pictureUrl}
          hashValue={topic.author._id}
          alt={topic.author.firstName}
          size={42}
          className="text-2xl"
        />

        <div className="flex flex-col">
          <span className="w-max text-sm font-bold text-gray-800 dark:text-gray-200">{topic.author.fullName}</span>
          <RelativeTime
            className="text-sm font-medium text-gray-500 dark:text-gray-400"
            timeStamp={parseISO(topic.createdAt)}
          />
        </div>

        <div className="flex items-center space-x-2">
          {topic.isPinned && <PinIcon className="h-4 w-4 fill-blue-50 dark:fill-blue-900/50" />}
        </div>

        {!isEditingContent && (
          <div className="col-start-2">
            <Editor editable={false} initialState={topic.bodyAsJson} />
          </div>
        )}

        {topic.__typename === "PostReply" && topic.answeredAt && (
          <div className="col-start-2">
            <div className="flex w-fit rounded-full border border-gray-300 bg-green-200 p-1 pl-0.5 dark:border-green-900 dark:bg-green-900/30">
              <div className="flex w-fit items-center space-x-1 px-1 text-green-600 dark:text-green-400">
                {topic.answeredAt && (
                  <CheckCircleIcon className="h-5 w-5 fill-transparent stroke-green-600 dark:fill-transparent dark:stroke-green-400" />
                )}

                <span className="flex font-aeonik text-xs font-bold tracking-wide">
                  <FormattedMessage defaultMessage="Marcado como resposta" id="ksfoHZ" />
                </span>
              </div>
            </div>
          </div>
        )}

        {viewer && isEditingContent && topic.viewerCanEdit && (
          <div className="col-start-2">
            <ForumEditPost
              onCancel={() => setIsEditingContent(!isEditingContent)}
              shortId={postShortId}
              objectId={topic.objectId}
              initialState={topic.bodyAsJson}
              isReply={topic.__typename === "PostReply"}
            />
          </div>
        )}

        <div className={classNames("col-span-2 col-start-2 my-2 grid grid-cols-[1fr_auto] items-start")}>
          <div className="flex gap-4 lg:justify-between lg:gap-2">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-2">
              <PostReactions
                reactable={topic}
                refetchQueries={refetchQueries}
                subjectId={topic._id}
                subjectType={topic.__typename === "ForumPost" ? IReactionSubject.Post : IReactionSubject.PostReply}
              />
            </div>

            <div className="flex items-center gap-2">
              {onReply && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild={true}>
                      <button
                        type="button"
                        onClick={onReply}
                        className="flex h-fit items-center rounded-lg border border-neutral-200 p-1 hover:border-blue-300 hover:bg-blue-50 dark:border-slate-600 dark:hover:border-blue-600 dark:hover:bg-blue-900/30"
                      >
                        <ReplyIcon className="h-5 w-5 text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      sideOffset={12}
                      className="max-w-xs rounded-xl border border-transparent bg-black text-white"
                    >
                      <strong className="font-bold">
                        <FormattedMessage
                          description="Funcionalidades do fórum"
                          defaultMessage="Responder"
                          id="jg7jzt"
                        />
                      </strong>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <div className="flex space-x-5">
                {viewerCanTakeActions && (
                  <ActionsDropdown>
                    {({ closeOnClick }) => (
                      <Menu>
                        {topic.viewerCanEdit && (
                          <MenuActionButtonItem
                            variant="primary"
                            onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                          >
                            <FormattedMessage
                              description="Funcionalidades do fórum"
                              defaultMessage="Editar"
                              id="S0+Yy2"
                            />
                          </MenuActionButtonItem>
                        )}

                        <MenuActionButtonItem
                          variant="primary"
                          onClick={closeOnClick(() => setIsOpenShare((prev) => !prev))}
                        >
                          <FormattedMessage
                            description="Funcionalidades do fórum"
                            defaultMessage="Compartilhar"
                            id="1cvwnp"
                          />
                        </MenuActionButtonItem>

                        {viewer && viewer.isSiteAdmin && (
                          <>
                            <MenuActionButtonItem
                              variant="primary"
                              onClick={closeOnClick(() => {
                                navigator.clipboard.writeText(topic.author.email);
                              })}
                            >
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Copiar email"
                                id="p4EQ6t"
                              />
                            </MenuActionButtonItem>

                            <MenuActionButtonItem
                              variant="primary"
                              onClick={closeOnClick(() => {
                                navigator.clipboard.writeText(topic.author._id);
                              })}
                            >
                              Copiar ID
                            </MenuActionButtonItem>

                            <MenuDivider />

                            <MenuTitle>
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Moderação"
                                id="gYbABk"
                              />
                            </MenuTitle>
                          </>
                        )}

                        <MenuDivider />

                        {topic.viewerCanDestroy && (
                          <MenuActionButtonItem
                            variant="danger"
                            disabled={isDestroyingPost}
                            onClick={() => {
                              if (topic.__typename === "PostReply") {
                                destroyPostReply({
                                  variables: {
                                    objectId: topic.objectId,
                                  },
                                  refetchQueries,
                                });
                              } else {
                                destroyPost({
                                  variables: {
                                    objectId: topic.objectId,
                                  },
                                  refetchQueries,
                                  onCompleted: () => {
                                    router.push("/black/forum");
                                  },
                                });
                              }
                            }}
                          >
                            {isDestroyingPost || isDestroyingPostReply ? (
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Apagando..."
                                id="jveZEJ"
                              />
                            ) : (
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Apagar"
                                id="PtzY6F"
                              />
                            )}
                          </MenuActionButtonItem>
                        )}

                        {topic.isPinned ? (
                          <MenuActionButtonItem
                            disabled={isUnpinningPost}
                            onClick={() => {
                              if (topic.__typename === "PostReply") {
                                unpinPostReply({
                                  variables: {
                                    objectId: topic.objectId,
                                  },
                                  refetchQueries,
                                });
                              } else {
                                unpinPost({
                                  variables: {
                                    objectId: topic.objectId,
                                  },
                                  refetchQueries,
                                });
                              }
                            }}
                          >
                            {isUnpinningPost || isUnpinningPostReply ? (
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Desfixando..."
                                id="AvTg0O"
                              />
                            ) : (
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Desfixar"
                                id="Y+QfRl"
                              />
                            )}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem
                            disabled={isPinningPost}
                            onClick={() => {
                              if (topic.__typename === "PostReply") {
                                pinPostReply({
                                  variables: {
                                    objectId: topic.objectId,
                                  },
                                  refetchQueries,
                                });
                              } else {
                                pinPost({
                                  variables: {
                                    objectId: topic.objectId,
                                  },
                                  refetchQueries,
                                });
                              }
                            }}
                          >
                            {isPinningPost || isPinningPostReply ? (
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Fixando..."
                                id="YOIKgM"
                              />
                            ) : (
                              <FormattedMessage
                                description="Funcionalidades do fórum"
                                defaultMessage="Fixar"
                                id="QkdAr3"
                              />
                            )}
                          </MenuActionButtonItem>
                        )}

                        {topic.__typename === "ForumPost" && viewer && viewer.isSiteAdmin && (
                          <>
                            {topic.isLocked && (
                              <MenuActionButtonItem
                                disabled={isUnlockingPost}
                                onClick={() =>
                                  unlockPost({
                                    variables: {
                                      objectId: topic.objectId,
                                    },
                                    refetchQueries,
                                  })
                                }
                              >
                                {isUnlockingPost ? (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Destrancando..."
                                    id="fjkZ5n"
                                  />
                                ) : (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Destrancar"
                                    id="KuRL54"
                                  />
                                )}
                              </MenuActionButtonItem>
                            )}

                            {!topic.isLocked && (
                              <MenuActionButtonItem
                                disabled={isLockingPost}
                                onClick={() =>
                                  lockPost({
                                    variables: {
                                      objectId: topic.objectId,
                                    },
                                    refetchQueries,
                                  })
                                }
                              >
                                {isLockingPost ? (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Trancando..."
                                    id="SFBxAS"
                                  />
                                ) : (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Trancar"
                                    id="Qs+xrd"
                                  />
                                )}
                              </MenuActionButtonItem>
                            )}
                          </>
                        )}

                        {topic.__typename === "PostReply" && viewerCanTakeAnswerActions && (
                          <>
                            {topic.answeredAt && (
                              <MenuActionButtonItem
                                disabled={isUnsettingAnswerPostReply}
                                onClick={() => {
                                  unsetPostReplyAnswer({
                                    variables: {
                                      objectId: topic.objectId,
                                    },
                                    refetchQueries,
                                  });
                                }}
                              >
                                {loadingAnswer ? (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Desmarcando..."
                                    id="Eg6aOk"
                                  />
                                ) : (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Desmarcar como resposta"
                                    id="5SGrjk"
                                  />
                                )}
                              </MenuActionButtonItem>
                            )}

                            {!topic.answeredAt && (
                              <MenuActionButtonItem
                                disabled={isSettingAnswerPostReply}
                                onClick={() => {
                                  setPostReplyAnswer({
                                    variables: {
                                      objectId: topic.objectId,
                                    },
                                    refetchQueries,
                                  });
                                }}
                              >
                                {loadingAnswer ? (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Marcando..."
                                    id="1046TS"
                                  />
                                ) : (
                                  <FormattedMessage
                                    description="Funcionalidades do fórum"
                                    defaultMessage="Marcar como resposta"
                                    id="OCpiWf"
                                  />
                                )}
                              </MenuActionButtonItem>
                            )}
                          </>
                        )}
                      </Menu>
                    )}
                  </ActionsDropdown>
                )}
              </div>
            </div>
          </div>
        </div>

        {children}
      </div>

      {topic.__typename === "PostReply" && topic.answeredBy && (
        <div className="absolute -bottom-6 left-4 z-10">
          <span className="font-aeonik text-sm font-medium text-green-600 dark:text-green-400">
            <FormattedMessage
              description="Funcionalidades do fórum"
              defaultMessage="Selecionado como reposta por"
              id="XOza0L"
            />{" "}
            <strong>{topic.answeredBy.fullName}</strong>
          </span>
        </div>
      )}

      <ForumShareDialog url={url} open={isOpenShare} toggle={() => setIsOpenShare((prev) => !prev)} />
    </div>
  );
}

interface ForumTopicCardSekeletonProps {
  className?: string;
}

export function ForumTopicCardSekeleton({ className }: ForumTopicCardSekeletonProps) {
  return (
    <div
      className={classNames(
        "col-span-4 h-72 w-full animate-pulse rounded bg-gray-200",
        "px-4 py-8 md:col-span-9 md:p-8",
        "dark:bg-slate-700",
        className,
      )}
    >
      <div className="grid h-full grid-cols-[auto_1fr_auto] items-start gap-x-5 gap-y-4">
        <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-slate-600" />

        <div className="flex h-full w-full flex-col space-y-5">
          <div className="flex flex-col space-y-0.5">
            <div className="flex h-4 w-36 rounded bg-gray-300 dark:bg-slate-600" />
            <div className="flex h-3 w-24 rounded bg-gray-300 dark:bg-slate-600" />
          </div>

          <div className="flex h-5/6 w-full rounded bg-gray-300 dark:bg-slate-600" />
        </div>

        <div className="h-3 w-10 rounded bg-gray-300 dark:bg-slate-600" />
      </div>
    </div>
  );
}

interface IForumShareDialogProps {
  url: string;
  open: boolean;
  toggle: () => void;
}

function ForumShareDialog({ url, open, toggle }: IForumShareDialogProps) {
  const [isCopied, setIsCopied] = useState(false);

  return (
    <Dialog open={open} onOpenChange={toggle}>
      <DialogContent className="max-h-screen sm:max-w-[425px] lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage description="Funcionalidades do fórum" defaultMessage="Compartilhar tópico" id="0NUvgZ" />
          </DialogTitle>

          <DialogDescription className="hidden">
            <FormattedMessage description="Funcionalidades do fórum" defaultMessage="Compartilhar tópico" id="0NUvgZ" />
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center space-x-4">
          <TextareaAutosize
            readOnly={true}
            value={url}
            className={classNames(
              "box-border flex h-10 w-full resize-none appearance-none rounded font-monospace",
              "border-none bg-slate-200 focus:ring-0",
              "dark:bg-slate-700",
            )}
          />

          <button
            type="button"
            className="h-10 rounded border bg-blue-500 p-1 px-2 text-white hover:bg-blue-600"
            onClick={() => {
              navigator.clipboard.writeText(url);
              setIsCopied((previous) => !previous);
            }}
          >
            {isCopied && <CheckIcon className="h-6 w-6" />}
            {!isCopied && <ClipboardIcon className="h-6 w-6" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
